/** @jsxImportSource preact */
import { AlertIcon } from "../icons";
import type { WidgetTokens } from "../theme";
import { Field } from "./base-ui.mjs";
import { formVars } from "./form-styles";

export interface TextFieldProps {
  tokens: WidgetTokens;
  label: string;
  /** 标签后面淡一点的补充，如 (optional)。 */
  labelNote?: string;
  /** 输入框左边的固定前缀，如订单号的 #。 */
  prefix?: string;
  name?: string;
  type?: "text" | "email";
  inputMode?: "text" | "email" | "numeric";
  autoComplete?: string;
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** 字段下方的提示。 */
  description?: string;
  /** 错误文字；有它就算无效，并替换提示。 */
  error?: string;
  invalid?: boolean;
  disabled?: boolean;
  /** 等宽字，订单号这类编号用。 */
  mono?: boolean;
}

/**
 * 表单输入框：标签、可选前缀、提示和错误。底层是 Base UI 的 Field，
 * 标签与输入框、提示与错误的关联（for、aria-describedby、aria-invalid）都由它处理。
 */
export function TextField({
  tokens: t,
  label,
  labelNote,
  prefix,
  name,
  type = "text",
  inputMode,
  autoComplete,
  placeholder,
  value,
  defaultValue,
  onValueChange,
  description,
  error,
  invalid = false,
  disabled = false,
  mono = false,
}: TextFieldProps) {
  const isInvalid = invalid || Boolean(error);
  return (
    <Field.Root
      className="gn-form-field"
      disabled={disabled}
      invalid={isInvalid}
      name={name}
      style={formVars(t)}
    >
      <Field.Label className="gn-form-label">
        {label}
        {labelNote && (
          <>
            {" "}
            <span className="gn-form-label-note">{labelNote}</span>
          </>
        )}
      </Field.Label>
      <div
        className="gn-form-wrap"
        data-disabled={disabled ? "" : undefined}
        data-invalid={isInvalid ? "" : undefined}
      >
        {prefix && (
          <span aria-hidden="true" className="gn-form-prefix">
            {prefix}
          </span>
        )}
        <Field.Control
          autoComplete={autoComplete}
          className="gn-form-input"
          data-mono={mono ? "" : undefined}
          defaultValue={defaultValue}
          inputMode={inputMode}
          onValueChange={(next) => onValueChange?.(next)}
          placeholder={placeholder}
          type={type}
          value={value}
        />
      </div>
      {error ? (
        <Field.Error className="gn-form-error" match role="alert">
          <AlertIcon size={14} />
          <span>{error}</span>
        </Field.Error>
      ) : (
        description && (
          <Field.Description className="gn-form-hint">
            {description}
          </Field.Description>
        )
      )}
    </Field.Root>
  );
}
